import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  EffectsBase,
  fromSocketEvent,
  SocketActions,
} from '@ps/core-data-access';
import {
  BuyerProgressChannelV3,
  SurveyCompletesChange,
  SurveyScoreChange,
  SurveyStatusChange,
} from '@ps/survey-utils';
import {
  concatMap,
  distinctUntilChanged,
  filter,
  from,
  map,
  mergeMap,
  of,
  pairwise,
  startWith,
} from 'rxjs';

import {
  selectActiveTabId,
  selectSurveyIds,
} from './survey-list/survey-list.selectors';
import { SURVEY_SOCKET_EVENTS } from '../utils/constants/survey-socket.constants';
import { SurveyListSocketUpdateActions } from './survey-list';
import { SurveyDashboardSocketDto } from '../api/dtos/survey-socket.dto';
import { SurveyDashboardSocketMapper } from '../api/mapper/survey-socket.mapper';
import { concatLatestFrom } from '@ngrx/operators';
import {
  ManageSurveyActions,
  ManageSurveySocketActions,
} from './manage-survey';

/**
 * Membership matches by the survey-id set, not array order. The list reorders
 * on every sort/pagination change while subscribing to the same surveys, so an
 * order-sensitive compare would churn unsubscribe→subscribe needlessly.
 */
function sameSurveyIdSet(left: number[], right: number[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  const known = new Set(left);
  return right.every((id) => known.has(id));
}

@Injectable()
export class SurveySocketEffects extends EffectsBase {
  private readonly _store = inject(Store);
  private readonly _actions$ = inject(Actions);
  private readonly _dashboardMapper = new SurveyDashboardSocketMapper();

  public readonly syncSurveyRooms$ = createEffect(() =>
    this._store.select(selectSurveyIds).pipe(
      distinctUntilChanged(sameSurveyIdSet),
      startWith<number[]>([]),
      pairwise(),
      filter(([previous, current]) => !sameSurveyIdSet(previous, current)),
      concatMap(([previous, current]) =>
        from(this._roomCommands(previous, current)),
      ),
    ),
  );

  public readonly surveyInProgressChange$ = createEffect(() =>
    fromSocketEvent<BuyerProgressChannelV3>(
      SURVEY_SOCKET_EVENTS.BUYER_IN_PROGRESS_CHANNEL,
    ).pipe(
      mergeMap(({ surveyId, actualBuyerInProgress }) => {
        const payload = {
          surveyId,
          survey: { inProgress: actualBuyerInProgress },
        };
        return of(
          SurveyListSocketUpdateActions.updateSurvey(payload),
          ManageSurveyActions.updateSurvey(payload),
        );
      }),
    ),
  );

  public readonly surveyCompleteChange$ = createEffect(() =>
    fromSocketEvent<SurveyCompletesChange>(
      SURVEY_SOCKET_EVENTS.SURVEY_COMPLETES,
    ).pipe(
      mergeMap(({ surveyId, completes }) => {
        const payload = {
          surveyId,
          survey: { completesRequired: completes },
        };
        return of(
          SurveyListSocketUpdateActions.updateSurvey(payload),
          ManageSurveyActions.updateSurvey(payload),
        );
      }),
    ),
  );

  public readonly surveyScoreChange$ = createEffect(() =>
    fromSocketEvent<SurveyScoreChange>(
      SURVEY_SOCKET_EVENTS.SURVEY_SCORE_CHANGE,
    ).pipe(
      mergeMap(({ surveyId, score, scorePercentage }) => {
        const payload = {
          surveyId,
          survey: { surveyScore: { score, scorePercentage } },
        };
        return of(
          SurveyListSocketUpdateActions.updateSurvey(payload),
          ManageSurveyActions.updateSurvey(payload),
        );
      }),
    ),
  );

  public readonly surveyStatusChange$ = createEffect(() =>
    fromSocketEvent<SurveyStatusChange>(
      SURVEY_SOCKET_EVENTS.SURVEY_STATUS_CHANGE,
    ).pipe(
      concatLatestFrom(() => this._store.select(selectActiveTabId)),
      mergeMap(([data, activeTabId]) => {
        const payload = {
          ...data,
          isAllTab: activeTabId === 'all',
        };
        return of(
          SurveyListSocketUpdateActions.filterSurveyByStatus(payload),
          ManageSurveyActions.updateSurvey({
            surveyId: data.surveyId,
            survey: { status: data.newStatus },
          }),
        );
      }),
    ),
  );

  public readonly dashboardDetailsChange$ = createEffect(() =>
    fromSocketEvent<SurveyDashboardSocketDto>(
      SURVEY_SOCKET_EVENTS.DASHBOARD_UPDATE,
    ).pipe(
      mergeMap((dto) => {
        const payload = this._dashboardMapper.fromDto(dto);
        return of(
          SurveyListSocketUpdateActions.updateSurvey(payload),
          ManageSurveyActions.updateSurvey(payload),
        );
      }),
    ),
  );

  public readonly trafficChannelUpdates$ = createEffect(() =>
    fromSocketEvent<number>(SURVEY_SOCKET_EVENTS.TRAFFIC_CHANNEL_UPDATES).pipe(
      map((surveyId) => ManageSurveyActions.loadTrafficChannels({ surveyId })),
    ),
  );

  public readonly joinQuotaRoom$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveySocketActions.joinQuotaTab),
      map(({ surveyId }) =>
        SocketActions.joinRoom({
          event: SURVEY_SOCKET_EVENTS.JOIN_QUOTA_TAB_ROOM,
          payload: surveyId,
        }),
      ),
    ),
  );

  public readonly leaveQuotaRoom$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveySocketActions.leaveQuotaTab),
      map(({ surveyId }) =>
        SocketActions.leaveRoom({
          event: SURVEY_SOCKET_EVENTS.LEAVE_QUOTA_TAB_ROOM,
          payload: surveyId,
        }),
      ),
    ),
  );

  public readonly joinTCTab$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveySocketActions.joinTCTab),
      map(({ surveyId }) =>
        SocketActions.joinRoom({
          event: SURVEY_SOCKET_EVENTS.JOIN_TC_TAB_ROOM,
          payload: surveyId,
        }),
      ),
    ),
  );

  public readonly leaveTCTab$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveySocketActions.leaveTCTab),
      map(({ surveyId }) =>
        SocketActions.leaveRoom({
          event: SURVEY_SOCKET_EVENTS.LEAVE_TC_TAB_ROOM,
          payload: surveyId,
        }),
      ),
    ),
  );

  public readonly joinSupplierTab$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveySocketActions.joinSupplierTab),
      map(({ surveyId }) =>
        SocketActions.joinRoom({
          event: SURVEY_SOCKET_EVENTS.JOIN_SUPPLIER_TAB_ROOM,
          payload: surveyId,
        }),
      ),
    ),
  );

  public readonly leaveSupplierTab$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveySocketActions.leaveSupplierTab),
      map(({ surveyId }) =>
        SocketActions.leaveRoom({
          event: SURVEY_SOCKET_EVENTS.LEAVE_SUPPLIER_TAB_ROOM,
          payload: surveyId,
        }),
      ),
    ),
  );

  public readonly joinSurveyRoom$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveySocketActions.joinSurveyRoom),
      map(({ surveyId }) =>
        SocketActions.joinRoom({
          event: SURVEY_SOCKET_EVENTS.JOIN_SURVEY_ROOM,
          payload: surveyId,
        }),
      ),
    ),
  );

  public readonly leaveSurveyRoom$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveySocketActions.leaveSurveyRoom),
      map(({ surveyId }) =>
        SocketActions.leaveRoom({
          event: SURVEY_SOCKET_EVENTS.LEAVE_SURVEY_ROOM,
          payload: surveyId,
        }),
      ),
    ),
  );

  public readonly subscribeTrafficSurveys$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveySocketActions.subscribeTrafficSurveys),
      map(({ surveyIds }) =>
        SocketActions.joinRoom({
          event: SURVEY_SOCKET_EVENTS.SUBSCRIBE_TO_SURVEYS,
          payload: { surveyIds },
        }),
      ),
    ),
  );

  public readonly unsubscribeTrafficSurveys$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveySocketActions.unsubscribeTrafficSurveys),
      map(({ surveyIds }) =>
        SocketActions.leaveRoom({
          event: SURVEY_SOCKET_EVENTS.UNSUBSCRIBE_FROM_SURVEYS,
          payload: { surveyIds },
        }),
      ),
    ),
  );

  private _roomCommands(previous: number[], current: number[]): Action[] {
    const commands: Action[] = [];
    if (previous.length) {
      commands.push(
        SocketActions.leaveRoom({
          event: SURVEY_SOCKET_EVENTS.UNSUBSCRIBE_FROM_SURVEYS,
          payload: { surveyIds: previous },
        }),
      );
    }
    if (current.length) {
      commands.push(
        SocketActions.joinRoom({
          event: SURVEY_SOCKET_EVENTS.SUBSCRIBE_TO_SURVEYS,
          payload: { surveyIds: current },
        }),
      );
    }
    return commands;
  }
}
